import { createContext, useContext, useMemo, useReducer } from "react";
import PropTypes from "prop-types";

const AppController = createContext();
AppController.displayName = "AppControllerContext";

function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV":          return { ...state, miniSidenav: action.value };
    case "TRANSPARENT_SIDENAV":   return { ...state, transparentSidenav: action.value };
    case "WHITE_SIDENAV":         return { ...state, whiteSidenav: action.value };
    case "SIDENAV_COLOR":         return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR":    return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR":          return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR":     return { ...state, openConfigurator: action.value };
    case "DIRECTION":             return { ...state, direction: action.value };
    case "LAYOUT":                return { ...state, layout: action.value };
    case "DARKMODE":              return { ...state, darkMode: action.value };
    default: throw new Error(`Unhandled action type: ${action.type}`);
  }
}

function AppControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: false,
    fixedNavbar: false,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: (() => {
      const stored = localStorage.getItem("hg-dark-mode");
      if (stored !== null) return stored === "true";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    })(),
  };

  const [controller, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <AppController.Provider value={value}>{children}</AppController.Provider>;
}

function useAppController() {
  const context = useContext(AppController);
  if (!context) {
    throw new Error("useAppController should be used inside the AppControllerProvider.");
  }
  return context;
}

AppControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const setMiniSidenav        = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav       = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor       = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar  = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar        = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator   = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection          = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout             = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode           = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export {
  AppControllerProvider,
  useAppController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};
